<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Edit Practice</h1>
    </div>
    <div class="modal-card-body">
      <div class="form-grid">
        <div class="form-row">
          <label for="practiceName" class="form-label label-required">Practice Name</label>
          <TextInput v-model="draft.name" placeholder="Enter practice name" />
        </div>

        <div class="form-row">
          <label for="address" class="form-label">Address (Full)</label>
          <TextareaInput v-model="draft.address" placeholder="e.g. 1000 Des Peres Road, Ste 110&#10;St. Louis, MO 63131" />
        </div>

        <div class="form-row">
          <label for="phone" class="form-label">Phone</label>
          <TextInput v-model="draft.phone" placeholder="e.g. (314) 942 - 8881" />
        </div>

        <div class="form-row">
          <label for="email" class="form-label">Email</label>
          <TextInput v-model="draft.email" type="email" placeholder="e.g. info@example.com" />
        </div>

        <div class="form-row">
          <label for="website" class="form-label">Website</label>
          <TextInput v-model="draft.website" placeholder="e.g. http://www.example.com" />
        </div>

        <div class="form-row">
          <label for="doctorName" class="form-label">Doctor Name</label>
          <TextInput v-model="draft.doctorName" placeholder="e.g. Clifford Strupp, MD" />
        </div>

        <div class="form-row">
          <label for="taxId" class="form-label">Tax ID</label>
          <TextInput v-model="draft.taxId" placeholder="e.g. 841834886" />
        </div>

        <div class="form-row">
          <label for="licenseNumber" class="form-label">License Number</label>
          <TextInput v-model="draft.licenseNumber" placeholder="e.g. 2017038041" />
        </div>

        <div class="form-row">
          <label for="npiNumber" class="form-label">NPI Number</label>
          <TextInput v-model="draft.npiNumber" placeholder="e.g. 1184136558" />
        </div>

        <div class="form-row">
          <label class="form-label">Practice Logo</label>
          <div class="logo-upload-container">
            <div v-if="logoPreview" class="logo-preview">
              <img :src="logoPreview" alt="Logo preview" />
              <button type="button" class="remove-logo-btn" @click="removeLogo">Remove</button>
            </div>
            <div v-else class="logo-placeholder">
              <span>No logo uploaded</span>
            </div>
            <input
              type="file"
              ref="logoInput"
              accept="image/*"
              @change="handleLogoSelect"
              style="display: none;"
            />
            <button type="button" class="button secondary" @click="$refs.logoInput.click()">
              {{ logoPreview ? 'Change Logo' : 'Upload Logo' }}
            </button>
          </div>
          <span class="form-hint">Image will be resized to max 256px height</span>
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click" @click="handleCancel">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting">
        {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import TextInput from '@/components/inputs/TextInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as api from '@/api';

export default {
  name: "UpdatePracticeModal",
  components: {
    TextInput,
    TextareaInput,
    CloseIcon
  },
  props: {
    practice: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'practice-updated'],
  data() {
    // Build full address from parts
    const addressParts = [];
    if (this.practice.addressStreet) addressParts.push(this.practice.addressStreet);
    if (this.practice.addressCity || this.practice.addressState || this.practice.addressZip) {
      const cityStateZip = [];
      if (this.practice.addressCity) cityStateZip.push(this.practice.addressCity);
      if (this.practice.addressState) cityStateZip.push(this.practice.addressState);
      if (this.practice.addressZip) cityStateZip.push(this.practice.addressZip);
      addressParts.push(cityStateZip.join(', '));
    }
    const fullAddress = addressParts.join('\n');

    return {
      draft: {
        name: this.practice.name || '',
        address: fullAddress,
        phone: this.practice.phone || '',
        email: this.practice.email || '',
        website: this.practice.website || '',
        doctorName: this.practice.doctorName || '',
        taxId: this.practice.taxId || '',
        licenseNumber: this.practice.licenseNumber || '',
        npiNumber: this.practice.npiNumber || '',
        logo: this.practice.logo || null
      },
      logoPreview: this.practice.logo || null,
      isSubmitting: false,
      error: null
    }
  },
  methods: {
    handleCancel() {
      // Close modal via close-on-click class
    },
    handleLogoSelect(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.error = 'Please select an image file';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Resize to max 256px height while maintaining aspect ratio
          const maxHeight = 256;
          let width = img.width;
          let height = img.height;

          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const base64 = canvas.toDataURL('image/png');
          this.draft.logo = base64;
          this.logoPreview = base64;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeLogo() {
      this.draft.logo = null;
      this.logoPreview = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = '';
      }
    },
    async handleSubmit() {
      this.error = null;

      if (!this.draft.name || this.draft.name.trim().length === 0) {
        this.error = 'Practice name is required';
        return;
      }

      this.isSubmitting = true;

      try {
        const updateData = {
          name: this.draft.name.trim(),
          addressStreet: this.draft.address.trim() || null,
          addressCity: null,
          addressState: null,
          addressZip: null,
          phone: this.draft.phone.trim() || null,
          email: this.draft.email.trim() || null,
          website: this.draft.website.trim() || null,
          doctorName: this.draft.doctorName.trim() || null,
          taxId: this.draft.taxId.trim() || null,
          licenseNumber: this.draft.licenseNumber.trim() || null,
          npiNumber: this.draft.npiNumber.trim() || null,
          logo: this.draft.logo || null
        };

        await api.updatePractice(this.practice.practiceId, updateData);
        this.$emit('practice-updated');
      } catch (err) {
        console.error('Failed to update practice:', err);
        this.error = err.message || 'Failed to update practice';
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.logo-upload-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-preview img {
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
  border: 1px solid #e5e5e5;
  border-radius: var(--border-radius);
  background: #fafafa;
}

.remove-logo-btn {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
}

.remove-logo-btn:hover {
  text-decoration: underline;
}

.logo-placeholder {
  padding: 16px 24px;
  border: 1px dashed #d4d4d4;
  border-radius: var(--border-radius);
  color: #9ca3af;
  font-size: 13px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
}
</style>
